import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { EmptyState } from './shared';

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Sansation:wght@400;700&display=swap');`;

const NAV_BLUE    = '#004AAD';
const GRAD_ACTIVE = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

const getImpactStyle = (impact) => {
  const l = (impact || '').toLowerCase();
  if (l.includes('high')) return { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' };
  if (l.includes('low'))  return { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' };
  return { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' };
};

const getEffortMeta = (effort) => {
  const l = (effort || '').toLowerCase();
  if (l.includes('low')  || l === '1') return { filled: 1, label: 'Low',    color: '#16a34a' };
  if (l.includes('high') || l === '3') return { filled: 3, label: 'High',   color: '#dc2626' };
  return                                       { filled: 2, label: 'Medium', color: '#ea580c' };
};

const deriveRecPriority = (rec) => {
  const imp = (rec.impact || '').toLowerCase();
  if (imp.includes('high')) return 'HIGH';
  if (imp.includes('low'))  return 'LOW';
  return 'MEDIUM';
};

const Recommendations = ({ recommendations, handleDownload }) => {
  const [recFilter, setRecFilter]               = useState('all');
  const [recView, setRecView]                   = useState('grid');
  const [selectedRecIndex, setSelectedRecIndex] = useState(0);
  const [isMobile, setIsMobile]                 = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet]                 = useState(window.innerWidth >= 640 && window.innerWidth < 1024);
  const recDetailRef                            = useRef(null);
  const [recDetailHeight, setRecDetailHeight]   = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!recDetailRef.current) return;
    const obs = new ResizeObserver(() => {
      if (recDetailRef.current) setRecDetailHeight(recDetailRef.current.offsetHeight);
    });
    obs.observe(recDetailRef.current);
    return () => obs.disconnect();
  }, [recDetailRef.current]);

  const allRecs      = recommendations.map(r => ({ ...r, _pri: r.priority_level || deriveRecPriority(r) }));
  const filteredRecs =
    recFilter === 'all'    ? allRecs :
    recFilter === 'high'   ? allRecs.filter(r => r._pri === 'HIGH')   :
    recFilter === 'medium' ? allRecs.filter(r => r._pri === 'MEDIUM') :
                             allRecs.filter(r => r._pri === 'LOW');
  const selRec = filteredRecs[selectedRecIndex] || filteredRecs[0];

  const filterTabs = [
    { key: 'all',    label: 'All Recommendations', count: allRecs.length },
    { key: 'high',   label: 'High Impact',          count: allRecs.filter(r => r._pri === 'HIGH').length   },
    { key: 'medium', label: 'Medium Impact',         count: allRecs.filter(r => r._pri === 'MEDIUM').length },
    { key: 'low',    label: 'Low Impact',            count: allRecs.filter(r => r._pri === 'LOW').length   },
  ].filter(f => f.key === 'all' || f.count > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
      <style>{FONT_CSS}</style>

      {/* ── Header ── */}
      <div style={{ 
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between', 
        marginBottom: isMobile ? 14 : 18, 
        flexWrap: 'wrap', 
        gap: isMobile ? 10 : 12,
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        {/* Title — Sansation */}
        <h2 style={{
          fontFamily: "'Sansation', sans-serif",
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 700, 
          color: '#0f172a', 
          margin: 0,
          letterSpacing: '-0.02em',
        }}>
          Expert Recommendations
        </h2>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 6 : 8,
          width: isMobile ? '100%' : 'auto',
        }}>
          {/* Grid / Detail toggle — inherit */}
          <div style={{ 
            display: 'flex', 
            border: '1px solid #e2e8f0', 
            borderRadius: isMobile ? 8 : 10, 
            overflow: 'hidden', 
            background: '#ffffff',
            width: isMobile ? '100%' : 'auto',
          }}>
            {[{ k: 'grid', l: 'Grid', icon: 'LayoutGrid' }, { k: 'detail', l: 'Detail', icon: 'Eye' }].map((b, bi) => {
              const isActive = recView === b.k;
              return (
                <button
                  key={b.k}
                  onClick={() => { setRecView(b.k); if (b.k === 'detail' && selectedRecIndex >= filteredRecs.length) setSelectedRecIndex(0); }}
                  style={{
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: isMobile ? 4 : 6,
                    padding: isMobile ? '6px 12px' : '7px 16px', 
                    fontSize: isMobile ? 11 : 13, 
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderRight: bi === 0 ? '1px solid #e2e8f0' : 'none',
                    border: 'none',
                    background: isActive ? GRAD_ACTIVE : '#ffffff',
                    color:      isActive ? '#ffffff'   : '#94a3b8',
                    fontFamily: 'inherit',
                    flex: isMobile ? 1 : 'none',
                    justifyContent: isMobile ? 'center' : 'flex-start',
                  }}
                >
                  <Icon name={b.icon} size={isMobile ? 12 : 14} color={isActive ? '#ffffff' : '#94a3b8'} />
                  {b.l}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Filter Pills — inherit ── */}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? 6 : 8, 
        marginBottom: isMobile ? 14 : 20, 
        flexWrap: 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isMobile ? 2 : 0,
      }}>
        {filterTabs.map(f => (
          <button
            key={f.key}
            onClick={() => { setRecFilter(f.key); setSelectedRecIndex(0); }}
            style={{
              padding: isMobile ? '6px 14px' : '7px 18px', 
              borderRadius: 999,
              fontSize: isMobile ? 11 : 13, 
              fontWeight: 600, 
              cursor: 'pointer',
              fontFamily: 'inherit',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              ...(recFilter === f.key
                ? { background: GRAD_ACTIVE, color: '#ffffff', border: 'none' }
                : { border: '1px solid #e2e8f0', background: '#ffffff', color: '#374151' }
              ),
            }}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {filteredRecs.length === 0 ? (
        <EmptyState icon="CheckCircle2" title="No recommendations" desc="Recommendations will populate from your HTML report." />
      ) : recView === 'grid' ? (

        /* ── Grid View ── */
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'), 
          gap: isMobile ? 12 : 14,
          width: '100%',
        }}>
          {filteredRecs.map((rec, i) => {
            const imp = getImpactStyle(rec.impact);
            const eff = getEffortMeta(rec.effort);
            const num = String(i + 1).padStart(2, '0');
            return (
              <div
                key={i}
                onClick={() => { setSelectedRecIndex(i); setRecView('detail'); }}
                style={{ 
                  border: '1px solid #e8edf2', 
                  borderRadius: isMobile ? 12 : 14, 
                  padding: isMobile ? '16px 14px' : '20px 20px 18px', 
                  background: '#ffffff', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  minHeight: isMobile ? 140 : 160, 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
                  transition: 'box-shadow 0.18s, transform 0.18s',
                  minWidth: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.09)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: isMobile ? 10 : 12, 
                  marginBottom: isMobile ? 10 : 14 
                }}>
                  {/* Number chip — inherit */}
                  <div style={{ 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32, 
                    borderRadius: isMobile ? 7 : 8, 
                    flexShrink: 0, 
                    background: imp.bg, 
                    border: `1px solid ${imp.border}`, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontFamily: 'inherit', 
                    fontSize: isMobile ? 11 : 12, 
                    fontWeight: 800, 
                    color: imp.color 
                  }}>{num}</div>
                  {/* Card title — Sansation */}
                  <p style={{ 
                    fontFamily: "'Sansation', sans-serif", 
                    fontSize: isMobile ? 13 : 14, 
                    fontWeight: 700, 
                    color: '#0f172a', 
                    lineHeight: 1.45, 
                    margin: 0, 
                    paddingTop: isMobile ? 1 : 2,
                    wordBreak: 'break-word',
                  }}>{rec.priority_action_name}</p>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: isMobile ? 5 : 6, 
                  flexWrap: 'wrap', 
                  flex: 1, 
                  paddingBottom: isMobile ? 10 : 14 
                }}>
                  {rec.impact && <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: 4, 
                    padding: isMobile ? '3px 8px' : '3px 10px', 
                    borderRadius: 999, 
                    fontSize: isMobile ? 10 : 11, 
                    fontWeight: 700, 
                    background: imp.bg, 
                    color: imp.color, 
                    border: `1px solid ${imp.border}`, 
                    fontFamily: 'inherit' 
                  }}><span style={{ fontSize: isMobile ? 9 : 10 }}>⚡</span>{rec.impact}</span>}
                  {rec.tag && <span style={{ 
                    padding: isMobile ? '3px 8px' : '3px 10px', 
                    borderRadius: 999, 
                    fontSize: isMobile ? 10 : 11, 
                    fontWeight: 500, 
                    background: '#f8fafc', 
                    color: '#64748b', 
                    border: '1px solid #e2e8f0', 
                    fontFamily: 'inherit' 
                  }}>{rec.tag}</span>}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  paddingTop: isMobile ? 10 : 14, 
                  borderTop: '1px solid #f1f5f9',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: isMobile ? 8 : 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 6 }}>
                    <span style={{ 
                      fontFamily: 'inherit', 
                      fontSize: isMobile ? 10 : 11, 
                      color: '#94a3b8', 
                      fontWeight: 500 
                    }}>Effort</span>
                    <div style={{ display: 'flex', gap: isMobile ? 2 : 3 }}>
                      {[0, 1, 2].map(seg => <div key={seg} style={{ 
                        width: isMobile ? 20 : 24, 
                        height: isMobile ? 4 : 5, 
                        borderRadius: 3, 
                        background: seg < eff.filled ? eff.color : '#e2e8f0' 
                      }} />)}
                    </div>
                    <span style={{ 
                      fontFamily: 'inherit', 
                      fontSize: isMobile ? 10 : 11, 
                      fontWeight: 700, 
                      color: eff.color 
                    }}>{eff.label}</span>
                  </div>
                  <span style={{ 
                    fontFamily: 'inherit', 
                    fontSize: isMobile ? 11 : 12, 
                    color: NAV_BLUE, 
                    fontWeight: 600 
                  }}>View Details →</span>
                </div>
              </div>
            );
          })}
        </div>

      ) : (

        /* ── Detail View ── */
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '1fr' : '1fr 300px'), 
          gap: isMobile ? 14 : 20,
          width: '100%',
        }}>
          <div ref={recDetailRef}>
            {selRec && (() => {
              const imp = getImpactStyle(selRec.impact);
              const eff = getEffortMeta(selRec.effort);
              return (
                <div style={{ 
                  border: `2px solid ${imp.border}`, 
                  borderRadius: isMobile ? 12 : 16, 
                  overflow: 'hidden', 
                  background: '#ffffff', 
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  minWidth: 0,
                }}>
                  <div style={{ 
                    padding: isMobile ? '18px 16px' : '24px 28px', 
                    borderBottom: '1px solid #f1f5f9' 
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      justifyContent: 'space-between', 
                      gap: isMobile ? 10 : 12, 
                      marginBottom: isMobile ? 10 : 14,
                      flexDirection: isMobile ? 'column' : 'row',
                    }}>
                      {/* Detail title — Sansation */}
                      <h3 style={{ 
                        fontFamily: "'Sansation', sans-serif", 
                        fontSize: isMobile ? 16 : 17, 
                        fontWeight: 700, 
                        color: '#0f172a', 
                        lineHeight: 1.4, 
                        flex: 1, 
                        margin: 0,
                        wordBreak: 'break-word',
                      }}>{selRec.priority_action_name}</h3>
                      {selRec.tag && <span style={{ 
                        fontFamily: 'inherit', 
                        padding: isMobile ? '3px 12px' : '4px 14px', 
                        borderRadius: 20, 
                        fontSize: isMobile ? 11 : 12, 
                        fontWeight: 600, 
                        background: '#f8fafc', 
                        color: '#64748b', 
                        border: '1px solid #e2e8f0', 
                        flexShrink: 0,
                        alignSelf: isMobile ? 'flex-start' : 'flex-start',
                      }}>{selRec.tag}</span>}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: isMobile ? 10 : 12, 
                      flexWrap: 'wrap' 
                    }}>
                      {selRec.impact && <span style={{ 
                        fontFamily: 'inherit', 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 5, 
                        padding: isMobile ? '3px 12px' : '4px 14px', 
                        borderRadius: 20, 
                        fontSize: isMobile ? 11 : 12, 
                        fontWeight: 700, 
                        background: imp.bg, 
                        color: imp.color, 
                        border: `1px solid ${imp.border}` 
                      }}>⚡ {selRec.impact}</span>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 6 }}>
                        <span style={{ 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 10 : 11, 
                          color: '#94a3b8' 
                        }}>Effort</span>
                        <div style={{ display: 'flex', gap: isMobile ? 2 : 3 }}>
                          {[0, 1, 2].map(seg => <div key={seg} style={{ 
                            width: isMobile ? 20 : 22, 
                            height: isMobile ? 5 : 6, 
                            borderRadius: 3, 
                            background: seg < eff.filled ? eff.color : '#e2e8f0' 
                          }} />)}
                        </div>
                        <span style={{ 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 10 : 11, 
                          fontWeight: 600, 
                          color: eff.color 
                        }}>{eff.label}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: isMobile ? '18px 16px' : '24px 28px' }}>
                    {/* Body text — inherit */}
                    <p style={{ 
                      fontFamily: 'inherit', 
                      fontSize: isMobile ? 13 : 14, 
                      color: '#475569', 
                      lineHeight: 1.75, 
                      margin: 0,
                      wordBreak: 'break-word',
                    }}>{selRec.priority_action_details}</p>
                    {selRec.slide_ref && (
                      <div style={{ 
                        marginTop: isMobile ? 14 : 20, 
                        padding: isMobile ? '10px 12px' : '12px 16px', 
                        background: '#f8fafc', 
                        borderRadius: isMobile ? 6 : 8, 
                        border: '1px solid #e2e8f0' 
                      }}>
                        <p style={{ 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 11 : 12, 
                          color: '#64748b', 
                          margin: 0 
                        }}>📍 Slide reference: <strong style={{ color: '#0f172a' }}>{selRec.slide_ref}</strong></p>
                      </div>
                    )}
                  </div>
                  <div style={{ 
                    padding: isMobile ? '12px 16px' : '14px 28px', 
                    borderTop: '1px solid #f1f5f9', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    background: '#fafafa',
                    gap: isMobile ? 8 : 0,
                  }}>
                    <button 
                      onClick={() => setSelectedRecIndex(Math.max(0, selectedRecIndex - 1))} 
                      disabled={selectedRecIndex === 0}
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 4 : 6, 
                        padding: isMobile ? '6px 10px' : '7px 14px', 
                        borderRadius: isMobile ? 6 : 8, 
                        border: '1px solid #e2e8f0', 
                        background: '#ffffff', 
                        fontSize: isMobile ? 11 : 13, 
                        fontWeight: 600, 
                        color: selectedRecIndex === 0 ? '#cbd5e1' : '#374151', 
                        cursor: selectedRecIndex === 0 ? 'not-allowed' : 'pointer', 
                        fontFamily: 'inherit',
                        flexShrink: 0,
                      }}>
                      <Icon name="ChevronLeft" size={isMobile ? 13 : 15} /> 
                      {!isMobile && 'Previous'}
                    </button>
                    <span style={{ 
                      fontFamily: 'inherit', 
                      fontSize: isMobile ? 11 : 13, 
                      color: '#94a3b8',
                      flexShrink: 0,
                    }}>{selectedRecIndex + 1} / {filteredRecs.length}</span>
                    <button 
                      onClick={() => setSelectedRecIndex(Math.min(filteredRecs.length - 1, selectedRecIndex + 1))} 
                      disabled={selectedRecIndex >= filteredRecs.length - 1}
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: isMobile ? 4 : 6, 
                        padding: isMobile ? '6px 10px' : '7px 14px', 
                        borderRadius: isMobile ? 6 : 8, 
                        border: '1px solid #e2e8f0', 
                        background: '#ffffff', 
                        fontSize: isMobile ? 11 : 13, 
                        fontWeight: 600, 
                        color: selectedRecIndex >= filteredRecs.length - 1 ? '#cbd5e1' : '#374151', 
                        cursor: selectedRecIndex >= filteredRecs.length - 1 ? 'not-allowed' : 'pointer', 
                        fontFamily: 'inherit',
                        flexShrink: 0,
                      }}>
                      {!isMobile && 'Next'} <Icon name="ChevronRight" size={isMobile ? 13 : 15} />
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right sidebar list - Hidden on mobile/tablet */}
          {!isMobile && !isTablet && (
            <div style={{ 
              position: 'sticky', 
              top: 0, 
              height: recDetailHeight || 500, 
              border: '1px solid #e2e8f0', 
              borderRadius: 14, 
              background: '#ffffff', 
              overflow: 'hidden', 
              display: 'flex', 
              flexDirection: 'column' 
            }}>
              {/* Sidebar header — Sansation */}
              <div style={{ 
                padding: '14px 16px', 
                background: '#f8fafc', 
                borderBottom: '1px solid #e2e8f0', 
                fontFamily: "'Sansation', sans-serif", 
                fontSize: 13, 
                fontWeight: 700, 
                color: '#0f172a', 
                flexShrink: 0 
              }}>
                All Recommendations ({filteredRecs.length})
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {filteredRecs.map((rec, i) => {
                  const imp = getImpactStyle(rec.impact);
                  const isActive = i === selectedRecIndex;
                  const num = String(i + 1).padStart(2, '0');
                  return (
                    <div
                      key={i}
                      onClick={() => setSelectedRecIndex(i)}
                      style={{ 
                        padding: '12px 16px', 
                        cursor: 'pointer', 
                        borderBottom: '1px solid #f1f5f9', 
                        borderLeft: isActive ? `3px solid ${NAV_BLUE}` : '3px solid transparent', 
                        background: isActive ? 'rgba(0,74,173,0.06)' : 'transparent', 
                        transition: 'all 0.15s' 
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                        <span style={{
                          width: 24, 
                          height: 24, 
                          borderRadius: 7, 
                          flexShrink: 0,
                          background: isActive ? GRAD_ACTIVE : imp.bg,
                          color: isActive ? '#ffffff' : imp.color,
                          fontFamily: 'inherit', 
                          fontSize: 10, 
                          fontWeight: 800,
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          border: isActive ? 'none' : `1px solid ${imp.border}`,
                        }}>{num}</span>
                        {/* Item title — Sansation */}
                        <p style={{ 
                          fontFamily: "'Sansation', sans-serif", 
                          fontSize: 12, 
                          fontWeight: 700, 
                          color: isActive ? NAV_BLUE : '#374151', 
                          lineHeight: 1.4, 
                          margin: 0 
                        }}>{rec.priority_action_name}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, paddingLeft: 32 }}>
                        {rec.impact && <span style={{ 
                          fontFamily: 'inherit', 
                          padding: '2px 8px', 
                          borderRadius: 20, 
                          fontSize: 10, 
                          fontWeight: 700, 
                          background: imp.bg, 
                          color: imp.color 
                        }}>⚡ {rec.impact}</span>}
                        {rec.tag && <span style={{ 
                          fontFamily: 'inherit', 
                          padding: '2px 8px', 
                          borderRadius: 20, 
                          fontSize: 10, 
                          fontWeight: 500, 
                          background: '#f1f5f9', 
                          color: '#64748b' 
                        }}>{rec.tag}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Recommendations;